<template>
  <div>
    <el-row>
      <el-col>
        <h3>{{ $t('specifications.title') }}</h3>
      </el-col>
    </el-row>

    <el-row
      type="flex"
      align="bottom"
    >
      <el-radio-group
        v-model="analysisLimitFormatRadio"
        :disabled="disabled"
        @change="onRadiogroupChange"
      >
        <el-col :span="6">
          <FormItem>
            <el-radio label="None">{{
              $t('specifications.limitFormats.absent')
            }}</el-radio>
          </FormItem>
        </el-col>
        <el-col :span="6">
          <FormItem>
            <el-radio label="FreeText">{{
              $t('specifications.limitFormats.freeText')
            }}</el-radio>
          </FormItem>
        </el-col>
        <el-col :span="6">
          <FormItem>
            <el-radio label="Selection">{{
              $t('specifications.limitFormats.selection')
            }}</el-radio>
          </FormItem>
        </el-col>
        <el-col :span="6">
          <FormItem>
            <el-radio label="Range">{{
              $t('specifications.limitFormats.range')
            }}</el-radio>
          </FormItem>
        </el-col>
      </el-radio-group>
    </el-row>

    <template v-if="analysisLimitFormatRadio === 'Selection'">
      <el-row :gutter="10">
        <el-col>
          <el-button
            :disabled="disabled || addSelectionValueDisabled"
            @click="addNewSelection"
            type="primary"
            size="medium"
            class="add-btn"
          >
            {{ $t('specifications.addSelection') }}
          </el-button>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col
          :span="6"
          v-for="(selection, index) in formValue.selectionValues"
          :key="index"
        >
          <FormItem>
            <el-input
              v-model="formValue.selectionValues[index]"
              :key="index"
              type="text"
              :disabled="disabled"
            >
              <i
                v-if="!disabled"
                @click="removeSelection(selection, index)"
                slot="suffix"
                class="el-input__icon el-icon-error"
              >
              </i>
            </el-input>
          </FormItem>
        </el-col>
      </el-row>
    </template>

    <template v-if="analysisLimitFormatRadio !== 'None'">
      <el-row
        :gutter="20"
        type="flex"
        align="middle"
      >
        <el-col
          :span="5"
          v-if="
            analysisLimitFormatRadio === 'FreeText' ||
            analysisLimitFormatRadio === 'Selection'
          "
        >
          <FormItem
            :label="$t('operations.create.specificationValue')"
            rules="required"
          >
            <template v-if="analysisLimitFormatRadio === 'FreeText'">
              <el-input
                v-model="formValue.specificationValue"
                type="text"
                :disabled="disabled"
              />
            </template>
            <template v-else-if="analysisLimitFormatRadio === 'Selection'">
              <el-select
                v-model="formValue.specificationValue"
                placeholder="Select"
                :disabled="disabled"
              >
                <el-option
                  v-for="(item, index) in formValue.selectionValues"
                  :key="index"
                  :label="item"
                  :value="item"
                ></el-option>
              </el-select>
            </template>
          </FormItem>
        </el-col>
        <el-col
          v-if="analysisLimitFormatRadio !== 'Range'"
          :span="5"
        >
          <FormItem :label="$t('specifications.measure')">
            <el-input
              type="text"
              v-model="measure"
              disabled
            />
          </FormItem>
        </el-col>
        <template v-if="analysisLimitFormatRadio === 'Range'">
          <el-col :span="5">
            <FormItem :label="$t('specifications.decimalValues')">
              <el-input
                v-model="formValue.decimalDigits"
                :disabled="disabled"
                @input="(val) => isNumber(val, 'decimalDigits')"
              />
            </FormItem>
          </el-col>
        </template>
      </el-row>
    </template>

    <template v-if="analysisLimitFormatRadio === 'Range'">
      <el-row
        :gutter="20"
        type="flex"
      >
        <el-col :span="5">
          <FormItem :label="$t('operations.create.infLimit')">
            <el-input
              v-model.number="formValue.minRange"
              :disabled="formValue.isMinRangeInclusive === null || disabled"
              size="small"
              @input="(val) => isNumber(val, 'minRange')"
            ></el-input>
          </FormItem>
        </el-col>
        <el-col :span="5">
          <FormItem :label="$t('specifications.lowerOperator')">
            <el-select
              v-model="formValue.isMinRangeInclusive"
              size="small"
              :disabled="disabled"
              @change="(val) => !val && (formValue.minRange = null)"
            >
              <el-option
                :label="$t('specifications.noOperator')"
                :value="null"
              />
              <el-option
                label="&lt;"
                :value="false"
              />
              <el-option
                label="&lt;="
                :value="true"
              />
            </el-select>
          </FormItem>
        </el-col>
        <el-col
          :span="4"
          class="centered-col"
        >
          {{ $t('specifications.create.value') }}
        </el-col>
        <el-col :span="5">
          <FormItem :label="$t('specifications.higherOperator')">
            <el-select
              v-model="formValue.isMaxRangeInclusive"
              size="small"
              :disabled="disabled"
              @change="(val) => !val && (formValue.maxRange = null)"
            >
              <el-option
                :label="$t('specifications.noOperator')"
                :value="null"
              />
              <el-option
                label="&lt;"
                :value="false"
              />
              <el-option
                label="&lt;="
                :value="true"
              />
            </el-select>
          </FormItem>
        </el-col>
        <el-col :span="5">
          <FormItem :label="$t('operations.create.supLimit')">
            <el-input
              v-model.number="formValue.maxRange"
              :disabled="formValue.isMaxRangeInclusive === null || disabled"
              size="small"
              @input="(val) => isNumber(val, 'maxRange')"
            ></el-input>
          </FormItem>
        </el-col>
      </el-row>
    </template>
  </div>
</template>

<script>
  import FormItem from '@/components/FormItem.vue';

  export default {
    name: 'FormSpecification',
    components: {
      FormItem,
    },
    props: {
      formValue: { type: Object, required: true },
      measure: { type: String, default: '' },
      disabled: { type: Boolean, default: false },
    },
    data() {
      return {
        analysisLimitFormatRadio: 'None',
      };
    },
    watch: {
      'formValue.type': {
        handler(data) {
          this.analysisLimitFormatRadio = data;
        },
        deep: true,
      },
    },
    computed: {
      addSelectionValueDisabled() {
        const selectionValuesCount = this.formValue.selectionValues
          ? this.formValue.selectionValues.length
          : 0;
        if (selectionValuesCount === 0) return false;
        return this.formValue.selectionValues[selectionValuesCount - 1] === '';
      },
    },
    created() {
      const { type } = this.formValue;
      if (type) this.analysisLimitFormatRadio = type;
    },
    methods: {
      // onSelectInputChange(index) {
      //   const valid = /^\d*\.?(?:\d{1,2})?$/;
      //   if (!valid.test(this.formValue.selectionValues[index])) {
      //     this.formValue.selectionValues[index] =
      //       this.formValue.selectionValues[index].slice(0, -1);
      //   } else {
      //     this.formValue.specificationValue = '';
      //   }
      // },
      onRadiogroupChange() {
        this.formValue.specificationValue = '';
        this.formValue.type = this.analysisLimitFormatRadio;
      },
      removeSelection(selection, index) {
        this.formValue.selectionValues.splice(index, 1);
        if (this.formValue.specificationValue === selection) {
          this.formValue.specificationValue = null;
        }
      },
      addNewSelection() {
        if (this.formValue.selectionValues) {
          this.formValue.selectionValues.push('');
        } else {
          this.formValue.selectionValues = [''];
        }
      },
      isNumber(value, field) {
        if (value === '') {
          this.formValue[field] = null;
          return;
        }
        const valid = /^(0|[1-9]\d*)?(\.\d*)?$/;

        const isValid = valid.test(value);
        const hasValue = this.formValue[field] != null;

        if (isValid) {
          this.formValue[field] =
            field === 'decimalDigits' ? Number(value) : value;
          return;
        }

        if (!isValid && hasValue) {
          this.formValue[field] =
            field === 'decimalDigits'
              ? Number(value.slice(0, -1))
              : value.slice(0, -1);
        }
      },
    },
  };
</script>

<style lang="scss">
  .el-radio-group {
    width: 100%;
  }

  .add-btn {
    margin-bottom: 20px;
  }

  .el-icon-error {
    color: red;

    &:hover {
      cursor: pointer;
    }
  }

  .el-dialog {
    .el-form-item {
      margin-bottom: 0;
    }
  }

  .range-input {
    display: flex;

    .el-radio {
      margin-top: 2rem;
      margin-right: 8px;

      &__label {
        display: none;
      }
    }

    label {
      margin-right: 5px;
    }
  }

  .centered-col {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spec-value {
    display: flex;

    .el-input {
      margin-right: 10px;
    }
  }
</style>
